// The posture score's trend as coordinates (#814). Pure, so the scale is tested
// without drawing: the SVG in `PosturePage.tsx` only joins what this returns.
//
// The vertical axis is fixed: its foot is nothing and its top is all that could be
// earned, and each point is read against its own maximum, because the ceiling moves
// when a component becomes measurable. A band of scores is never stretched to fill
// the height, so a flat trend looks flat. The horizontal axis is time.
//
// Two scores are one measurement only when one formula produced them over one set of
// components (#1283): the trend is drawn as segments, and no line joins two points
// that are not.

import type { TrendPoint } from "../api/posture";

export interface TrendCoordinate {
  readonly x: number;
  readonly y: number;
  readonly point: TrendPoint;
}

/** What a score was computed with: the formula's version and the components it scored. */
export interface Formula {
  readonly formula_version: number;
  readonly scored_components: readonly string[];
}

const componentSet = (f: Formula): string => [...new Set(f.scored_components)].sort().join(",");

/** Whether two scores may be compared: one formula version over one set of components. */
export function sameFormula(a: Formula, b: Formula): boolean {
  return a.formula_version === b.formula_version && componentSet(a) === componentSet(b);
}

const share = (p: TrendPoint): number =>
  p.score_max > 0 ? Math.min(1, Math.max(0, p.score / p.score_max)) : 0;

/** Where each point sits in a `width` by `height` box, oldest first; SVG's y grows downward. */
export function trendCoordinates(
  points: readonly TrendPoint[],
  box: { width: number; height: number },
): TrendCoordinate[] {
  const timed = points
    .map((point) => ({ point, at: Date.parse(point.computed_at) }))
    .sort((a, b) => a.at - b.at);
  if (timed.length === 0) return [];
  const first = timed[0]!.at;
  const span = timed[timed.length - 1]!.at - first;
  return timed.map(({ point, at }) => ({
    x: span === 0 ? box.width / 2 : ((at - first) / span) * box.width,
    y: (1 - share(point)) * box.height,
    point,
  }));
}

/**
 * The coordinates split into runs of consecutive points that share a formula, oldest
 * first, on the time axis the whole trend shares. A line is drawn within a run, never
 * between two.
 */
export function trendSegments(
  points: readonly TrendPoint[],
  box: { width: number; height: number },
): TrendCoordinate[][] {
  const segments: TrendCoordinate[][] = [];
  for (const c of trendCoordinates(points, box)) {
    const current = segments[segments.length - 1];
    const last = current?.[current.length - 1];
    if (current !== undefined && last !== undefined && sameFormula(last.point, c.point)) {
      current.push(c);
    } else {
      segments.push([c]);
    }
  }
  return segments;
}
